import { useState, type ReactNode } from 'react';
import {
  Image, Pressable, ScrollView, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { Text, TextInput } from '@/components/common/AppText';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { ChevronDown, Search, X } from 'lucide-react-native';
import { borderRadius, colors, motion, spacing } from '@tabbio/tokens';
import { useHaptics } from '../../hooks/useHaptics';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';

// ─── Constants ──────────────────────────────────────────────────────

export const GROUP_RADIUS = 28;

export const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

// ─── Row shell ──────────────────────────────────────────────────────

export function getGroupedRowStyle(
  index: number,
  totalCount: number,
  backgroundColor: string,
  borderColor: string,
) {
  const isFirst = index === 0;
  const isLast = index === totalCount - 1;

  return {
    backgroundColor,
    borderColor,
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderRightWidth: StyleSheet.hairlineWidth,
    borderTopWidth: isFirst ? StyleSheet.hairlineWidth : 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderTopLeftRadius: isFirst ? GROUP_RADIUS : 0,
    borderTopRightRadius: isFirst ? GROUP_RADIUS : 0,
    borderBottomLeftRadius: isLast ? GROUP_RADIUS : 0,
    borderBottomRightRadius: isLast ? GROUP_RADIUS : 0,
  } as const;
}

// ─── FeedSearchBar ──────────────────────────────────────────────────

interface FeedSearchBarProps {
  value: string;
  onChangeText: (text: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
}

export function FeedSearchBar({ value, onChangeText, onSubmit, placeholder }: FeedSearchBarProps) {
  const { theme } = useTheme();
  const { isRTL } = useLocale();
  const [focused, setFocused] = useState(false);

  return (
    <View style={styles.searchContainer}>
      <View
        style={[
          styles.searchBar,
          {
            direction: isRTL ? 'rtl' : 'ltr',
            backgroundColor: theme.surfaceSecondary,
            borderColor: focused ? theme.border : 'transparent',
          },
        ]}
      >
        <Search size={16} color={theme.textTertiary} />
        <TextInput
          style={[styles.searchInput, { color: theme.text }]}
          placeholder={placeholder}
          placeholderTextColor={theme.textTertiary}
          value={value}
          onChangeText={onChangeText}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onSubmitEditing={onSubmit}
          returnKeyType="search"
          autoCorrect={false}
        />
        {value.length > 0 ? (
          <Pressable onPress={() => onChangeText('')} hitSlop={6}>
            <X size={14} color={theme.textTertiary} />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

// ─── FeedFilterPill ─────────────────────────────────────────────────

interface FeedFilterPillProps {
  label: string;
  active: boolean;
  onPress: () => void;
}

export function FeedFilterPill({ label, active, onPress }: FeedFilterPillProps) {
  const { theme } = useTheme();

  return (
    <Pressable
      onPress={onPress}
      style={[
        styles.filterPill,
        {
          backgroundColor: active ? colors.primary : 'transparent',
          borderColor: active ? colors.primary : theme.border,
        },
      ]}
    >
      <Text style={[styles.filterText, { color: active ? '#ffffff' : theme.text }]}>
        {label}
      </Text>
      <ChevronDown
        size={14}
        color={active ? '#ffffff' : theme.textTertiary}
        strokeWidth={1.75}
      />
    </Pressable>
  );
}

// ─── FeedFilterRow ──────────────────────────────────────────────────

interface FeedFilterRowProps {
  children: ReactNode;
}

export function FeedFilterRow({ children }: FeedFilterRowProps) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.filtersContainer}
    >
      {children}
    </ScrollView>
  );
}

// ─── FeedSegmentedTabs ──────────────────────────────────────────────

interface FeedTab {
  key: string;
  label: string;
}

interface FeedSegmentedTabsProps {
  tabs: FeedTab[];
  activeKey: string;
  onTabPress: (key: string) => void;
}

export function FeedSegmentedTabs({ tabs, activeKey, onTabPress }: FeedSegmentedTabsProps) {
  const { theme } = useTheme();

  return (
    <View style={[styles.tabContainer, { backgroundColor: theme.surfaceSecondary }]}>
      {tabs.map((tab) => (
        <Pressable
          key={tab.key}
          onPress={() => onTabPress(tab.key)}
          style={[
            styles.tab,
            activeKey === tab.key ? { backgroundColor: theme.background } : null,
          ]}
        >
          <Text
            style={[
              styles.tabText,
              { color: activeKey === tab.key ? theme.text : theme.textSecondary },
            ]}
          >
            {tab.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );
}

// ─── FeedGroupedRow ─────────────────────────────────────────────────

interface FeedGroupedRowProps {
  index: number;
  totalCount: number;
  onPress?: () => void;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}

export function FeedGroupedRow({
  index,
  totalCount,
  onPress,
  children,
  style,
}: FeedGroupedRowProps) {
  const { theme, isDark } = useTheme();
  const haptics = useHaptics();
  const scale = useSharedValue(1);
  const animStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));
  const surfaceColor = isDark ? theme.surfaceElevated : 'rgba(255,255,255,0.82)';

  return (
    <AnimatedPressable
      onPress={() => {
        haptics.trigger('light');
        onPress?.();
      }}
      onPressIn={() => {
        scale.value = withTiming(motion.press.scale, { duration: motion.duration.instant });
      }}
      onPressOut={() => {
        scale.value = withTiming(1, { duration: motion.duration.fast });
      }}
      style={[
        animStyle,
        styles.groupedRow,
        getGroupedRowStyle(index, totalCount, surfaceColor, theme.borderSubtle),
        style,
      ]}
    >
      {children}
    </AnimatedPressable>
  );
}

// ─── FeedAvatar ─────────────────────────────────────────────────────

const DEFAULT_USER_AVATAR = require('../../../assets/default-avatar.png');

interface FeedAvatarProps {
  initials?: string;
  size?: number;
  icon?: ReactNode;
  imageUrl?: string | null;
}

export function FeedAvatar({ size = 48, icon, imageUrl }: FeedAvatarProps) {
  const { theme } = useTheme();
  const half = size / 2;

  return (
    <View
      style={[
        styles.avatar,
        {
          width: size,
          height: size,
          borderRadius: half,
          borderColor: theme.borderSubtle,
          backgroundColor: theme.surfaceSecondary,
        },
      ]}
    >
      {icon ?? (
        <Image
          source={imageUrl ? { uri: imageUrl } : DEFAULT_USER_AVATAR}
          style={{ width: size, height: size, borderRadius: half }}
          resizeMode="cover"
        />
      )}
    </View>
  );
}

// ─── FeedInfoPill ───────────────────────────────────────────────────

interface FeedInfoPillProps {
  text: string;
  icon?: ReactNode;
}

export function FeedInfoPill({ text, icon }: FeedInfoPillProps) {
  const { theme } = useTheme();

  return (
    <View
      style={[
        styles.infoPill,
        {
          backgroundColor: theme.surface,
          borderColor: theme.borderSubtle,
        },
      ]}
    >
      {icon}
      <Text style={[styles.infoPillText, { color: theme.textSecondary }]} numberOfLines={1}>
        {text}
      </Text>
    </View>
  );
}

// ─── FeedActionPill ─────────────────────────────────────────────────

interface FeedActionPillProps {
  label: string;
  onPress?: () => void;
}

export function FeedActionPill({ label, onPress }: FeedActionPillProps) {
  return (
    <Pressable
      onPress={onPress}
      style={[
        styles.actionPill,
        {
          backgroundColor: colors.primary,
          borderColor: colors.primary,
        },
      ]}
    >
      <Text style={styles.actionText}>{label}</Text>
    </Pressable>
  );
}

// ─── FeedEmptyState ─────────────────────────────────────────────────

interface FeedEmptyStateProps {
  title: string;
  subtitle?: string;
}

export function FeedEmptyState({ title, subtitle }: FeedEmptyStateProps) {
  const { theme } = useTheme();

  return (
    <View style={styles.centerState}>
      <Text style={[styles.emptyTitle, { color: theme.text }]}>{title}</Text>
      {subtitle ? (
        <Text style={[styles.emptySubtitle, { color: theme.textSecondary }]}>{subtitle}</Text>
      ) : null}
    </View>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  // Search
  searchContainer: {
    paddingTop: spacing[2],
    paddingBottom: spacing[1],
  },
  searchBar: {
    height: 44,
    borderRadius: borderRadius.full,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    paddingHorizontal: spacing[4],
    borderWidth: StyleSheet.hairlineWidth,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
  },

  // Filters
  filtersContainer: {
    gap: spacing[2],
    paddingVertical: spacing[3],
  },
  filterPill: {
    minHeight: 36,
    borderWidth: 1,
    borderRadius: borderRadius.full,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  filterText: {
    fontSize: 14,
    fontWeight: '500',
  },

  // Tabs
  tabContainer: {
    flexDirection: 'row',
    borderRadius: borderRadius.full,
    padding: 3,
    marginBottom: spacing[4],
  },
  tab: {
    flex: 1,
    minHeight: 40,
    borderRadius: borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
  },

  // Grouped row
  groupedRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[4],
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[4],
    overflow: 'hidden',
  },

  // Avatar
  avatar: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },

  // Info pill
  infoPill: {
    alignSelf: 'flex-start',
    maxWidth: '92%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: borderRadius.full,
    borderWidth: StyleSheet.hairlineWidth,
    marginBottom: spacing[2],
  },
  infoPillText: {
    fontSize: 12,
    fontWeight: '500',
    flexShrink: 1,
  },

  // Action pill
  actionPill: {
    minHeight: 36,
    paddingHorizontal: 16,
    borderRadius: borderRadius.full,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  actionText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Empty state
  centerState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing[6],
    paddingBottom: 80,
    gap: spacing[2],
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: '600',
  },
  emptySubtitle: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
  },
});
